
// Simple ID generator if uuid not available/wanted
const generateId = () => Math.random().toString(36).substring(2, 10) + Date.now().toString(36);

export type RunSummary = {
    regime?: string | null;
    probate?: string; // yes/no/unknown
    iht?: string;     // confirmed/pending
    pathway?: string; // action pack pathway
};

export type RunHistoryItem = {
    run_id: string;
    ran_at: string; // ISO
    summary: RunSummary;
};

export type CaseFile = {
    case_id: string;
    title: string;
    created_at: string;
    updated_at: string;

    case_input: any; // User's working state (EstateProfile, EstateAssets, etc.)

    last_result?: any; // The full response from the last audit run
    run_history: RunHistoryItem[];
};

const STORAGE_KEY_PREFIX = 'probate_cases_v1';

// User-scoped storage. Cases are namespaced per logged-in user so that
// multiple accounts on the same browser cannot see each other's data.
let currentUserId: string | null = null;

export const setCurrentCaseStorageUser = (userId: string | null) => {
    currentUserId = userId;
};

const storageKey = () => `${STORAGE_KEY_PREFIX}:${currentUserId ?? 'anon'}`;

// --- Internal Helper ---
const loadAll = (): CaseFile[] => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = localStorage.getItem(storageKey());
        if (!raw) return [];
        return JSON.parse(raw);
    } catch (e) {
        console.error("Failed to load cases", e);
        return [];
    }
};

const saveAll = (cases: CaseFile[]) => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(storageKey(), JSON.stringify(cases));
    } catch (e) {
        console.error("Failed to save cases", e);
    }
};

// One-time migration: if a legacy unscoped key exists and the current user
// has no cases yet, adopt the legacy data for this user. Runs at most once
// per user per session.
const migratedUsers = new Set<string>();
export const migrateLegacyCasesIfNeeded = () => {
    if (typeof window === 'undefined') return;
    if (!currentUserId) return;
    if (migratedUsers.has(currentUserId)) return;
    migratedUsers.add(currentUserId);
    try {
        const legacy = localStorage.getItem(STORAGE_KEY_PREFIX);
        if (!legacy) return;
        const existing = localStorage.getItem(storageKey());
        if (!existing) {
            localStorage.setItem(storageKey(), legacy);
        }
        // Remove legacy key so other accounts on this browser don't inherit it.
        localStorage.removeItem(STORAGE_KEY_PREFIX);
    } catch (e) {
        console.error("Legacy case migration failed", e);
    }
};

// --- Public API ---

export const listCases = (): CaseFile[] => {
    return loadAll().sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
};

export const getCase = (caseId: string): CaseFile | null => {
    const cases = loadAll();
    return cases.find(c => c.case_id === caseId) || null;
};

export const createCase = (title: string = "New Case", initialData: any = {}): CaseFile => {
    const newCase: CaseFile = {
        case_id: generateId(),
        title,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        case_input: initialData,
        run_history: []
    };
    const cases = loadAll();
    cases.push(newCase);
    saveAll(cases);
    return newCase;
};

export const updateCase = (caseId: string, updates: Partial<CaseFile>): CaseFile | null => {
    const cases = loadAll();
    const idx = cases.findIndex(c => c.case_id === caseId);
    if (idx === -1) return null;

    const updated = {
        ...cases[idx],
        ...updates,
        updated_at: new Date().toISOString()
    };
    cases[idx] = updated;
    saveAll(cases);
    return updated;
};

export const deleteCase = (caseId: string): void => {
    const cases = loadAll();
    const newCases = cases.filter(c => c.case_id !== caseId);
    saveAll(newCases);
};

export const saveRunToHistory = (caseId: string, result: any, summary: RunSummary): CaseFile | null => {
    const cases = loadAll();
    const idx = cases.findIndex(c => c.case_id === caseId);
    if (idx === -1) return null;

    const c = cases[idx];

    // Create history item
    const historyItem: RunHistoryItem = {
        run_id: generateId(),
        ran_at: new Date().toISOString(),
        summary
    };

    // Update case
    // Cap history at 20
    const newHistory = [historyItem, ...(c.run_history || [])].slice(0, 20);

    const updated: CaseFile = {
        ...c,
        last_result: result,
        run_history: newHistory,
        updated_at: new Date().toISOString()
    };

    cases[idx] = updated;
    saveAll(cases);
    return updated;
};

export const importCase = (fileContent: string): CaseFile | null => {
    try {
        const parsed = JSON.parse(fileContent);

        // Validation: Must have case_input at minimum
        if (!parsed.case_input && !parsed.EstateProfile) {
            // Might be a raw input file (legacy) -> wrap it
            return createCase("Imported Case", parsed);
        }

        // It looks like a proper CaseFile?
        // Check for case_id. If specific logic to avoid collisions is needed:
        // For now, ALWAYS import as a new copy to avoid overwriting existing data by accident.
        // Unless we implement overwrite confirmation logic in UI. 
        // Plan says: "Import as new case" default.

        const newTitle = parsed.title ? `${parsed.title} (Imported)` : "Imported Case";

        // Create fresh to ensure schema compliance
        const newCase: CaseFile = {
            case_id: generateId(),
            title: newTitle,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            case_input: parsed.case_input || parsed, // Fallback if structure weird
            last_result: parsed.last_result,
            run_history: Array.isArray(parsed.run_history) ? parsed.run_history : []
        };

        const cases = loadAll();
        cases.push(newCase);
        saveAll(cases);
        return newCase;

    } catch (e) {
        console.error("Import failed", e);
        return null;
    }
};
